import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-light-svg-icons";
import { faChevronLeft, faChevronRight, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Dictionary, groupBy, range } from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ButtonNew as Button } from "components/buttonNew";
import DatePickerBlurTitleInput from "./DatePickerBlurTitleInput";
import Portal from "elements/Portal";
import "./datePickerInput.scss";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { capitalizeWords } from "helpers/Helpers";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ReactDOM from "react-dom";

export type DatePickerMonthsShown = 1 | 2;
export type DatePickerPosition = "left" | "center" | "right";
type DatePickerSize = "small" | "medium" | "large";

interface IDatePickerInputProps {
  label?: string;
  months: DatePickerMonthsShown;
  position: DatePickerPosition;
  verticalPosition?: "bottom" | "top";
  size?: DatePickerSize;
  centeredInputText?: boolean;
  rightAlignedCalendar?: boolean;
  showQuickOptions?: boolean;
  startingDate?: Date;
  setStartingDate?: React.Dispatch<React.SetStateAction<Date>>;
  endingDate?: Date;
  setEndingDate?: React.Dispatch<React.SetStateAction<Date>>;
  multipleDates?: Date[];
  setMultipleDates?: (multipleDates: Date[]) => void;
  error?: boolean;
  disablePastDays?: boolean;
  disableFutureDays?: boolean;
  showBlurTitleOnError?: boolean;
  shorterDateString?: boolean;
  maxDayRange?: number;
  helpText?: string;
  disabled?: boolean;
  /**Render dropdown outside root DOM using <Portal> component. Set to true for dropdowns used inside modals that overflow */
  usePortal?: boolean;
  portalId?: string;
  requireApply?: boolean;
  iconOnlyTrigger?: boolean;
  allowEmptySingleDate?: boolean;
  allowClear?: boolean;
  monthOnlyCalendar?: boolean;
  hideDaysOfWeek?: boolean;
  hideQuickOptions?: boolean;
}

interface IDatePickerInputState {
  rangeDatePickerInputContainerFocused: boolean;
  singleDatePickerInputContainerFocused: boolean;
  multipleDatePickerInputContainerFocused: boolean;
  datePickerRef: React.MutableRefObject<HTMLDivElement>;
  datePickerOpen: boolean;
  datePickerInput: string;
  firstVisibleMonth: Date;
  selectedStartingDate: Date;
  selectedStartingDateInput: string;
  selectedEndingDate: Date;
  selectedEndingDateInput: string;
  selectedQuickOption: number;
}

interface IDatePickerInputDateRange {
  start: Date;
  end: Date;
}

interface IQuickOption {
  label: string;
  onClick: () => void;
}

type DatePickerView = "single" | "range" | "multiple" | "invalid";

export default function DatePickerInput(props: IDatePickerInputProps) {
  const {
    label,
    months,
    position,
    size = "medium",
    centeredInputText,
    rightAlignedCalendar,
    showQuickOptions,
    startingDate,
    setStartingDate,
    endingDate,
    setEndingDate,
    multipleDates,
    setMultipleDates,
    error,
    disablePastDays,
    disableFutureDays,
    showBlurTitleOnError,
    shorterDateString,
    maxDayRange,
    helpText,
    disabled = false,
    verticalPosition = "bottom",
    usePortal,
    portalId,
    requireApply,
    iconOnlyTrigger,
    allowEmptySingleDate,
    allowClear,
    monthOnlyCalendar,
    hideDaysOfWeek,
    hideQuickOptions,
  } = props;

  const windowSize = useWindowSize();
  const { t, i18n } = useTranslation();

  const isTabletSize = windowSize.width <= TABLET_WIDTH;
  const mobilePortalRef = useRef<HTMLDivElement>(null);
  const desktopPickerPanelRef = useRef<HTMLDivElement>(null);
  const [desktopPickerMaxHeight, setDesktopPickerMaxHeight] = useState<number | null>(null);
  const [desktopPickerOpenAbove, setDesktopPickerOpenAbove] = useState(false);

  function determineDatePickerView(): DatePickerView {
    if (startingDate && endingDate) {
      return "range";
    } else if (multipleDates) {
      return "multiple";
    } else if (startingDate || allowEmptySingleDate) {
      return "single";
    } else {
      return "invalid";
    }
  }

  const datePickerView = determineDatePickerView();

  const FORMAT_DATE_STRING_REGEX = /^([0-9][0-9])\/([0-9][0-9])\/([1-9][0-9]{3})$/;
  const FORMAT_DATE_STRING_NO_YEAR_REGEX = /^(1[0-2]|0?[1-9])\/(3[01]|[12][0-9]|0?[1-9])$/;

  const [datePickerInputState, setDatePickerInputState] = useState<IDatePickerInputState>({
    rangeDatePickerInputContainerFocused: false,
    singleDatePickerInputContainerFocused: false,
    multipleDatePickerInputContainerFocused: false,
    datePickerRef: useRef<HTMLDivElement>(null),
    datePickerOpen: false,
    datePickerInput: "",
    firstVisibleMonth: undefined,
    selectedStartingDate: undefined,
    selectedStartingDateInput: "",
    selectedEndingDate: undefined,
    selectedEndingDateInput: "",
    selectedQuickOption: undefined,
  });

  const quickOptions: IQuickOption[] = [
    { label: t("Today"), onClick: selectToday },
    { label: t("Yesterday"), onClick: selectYesterday },
    { label: t("This week"), onClick: selectThisWeek },
    { label: t("Last week"), onClick: selectLastWeek },
    { label: t("Past 7 days"), onClick: selectPast7Days },
    { label: t("This month"), onClick: selectThisMonth },
    { label: t("Last month"), onClick: selectLastMonth },
    { label: t("This year"), onClick: selectThisYear },
    { label: t("Last year"), onClick: selectLastYear },
  ];

  function dateToFormattedDateString(date: Date) {
    if (!date) {
      return "";
    } else {
      const month = String(date.getMonth() + 1);
      const day = String(date.getDate());
      const year = String(date.getFullYear());

      // Format string causes the leading 0 to display first.
      const formatString = `${month.length === 1 ? "0" + month : month}/${
        day.length === 1 && !monthOnlyCalendar ? "0" + day : day
      }${monthOnlyCalendar ? "" : `/${year}`}`;

      return formatString;
    }
  }

  function dateToReadableString(date: Date) {
    if (!date || isNaN(date.getTime())) {
      return "";
    } else {
      return date.toLocaleDateString(i18n.language ?? "en-CA", {
        month: "short",
        day: "numeric",
        ...(!monthOnlyCalendar && { year: "numeric" }),
      });
    }
  }

  function handleOutsideDatePickerClick(event: MouseEvent | TouchEvent) {
    const target = event.target as Node;
    const containsTarget = Boolean(
      (datePickerInputState.datePickerRef.current && datePickerInputState.datePickerRef.current.contains(target)) ||
        (mobilePortalRef.current && mobilePortalRef.current.contains(target)),
    );
    if (
      !datePickerInputState.datePickerRef.current ||
      containsTarget ||
      (usePortal && dropdownRef?.current?.contains(event?.target as Node))
    ) {
      return;
    }

    setDatePickerInputState(prev => ({ ...prev, datePickerOpen: false }));
  }

  const [selectPosition, setSelectPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const scrollableParentElementRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const el = datePickerInputState.datePickerRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const top = rect.bottom;
    const left = rect.left;
    setSelectPosition({ top, left });
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideDatePickerClick);
    document.addEventListener("touchstart", handleOutsideDatePickerClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideDatePickerClick);
      document.removeEventListener("touchstart", handleOutsideDatePickerClick);
    };
  }, []);

  useLayoutEffect(() => {
    if (!usePortal) {
      return;
    }
    if (datePickerInputState.datePickerOpen) {
      updatePosition();
    }
  }, [datePickerInputState.datePickerOpen, updatePosition, usePortal]);

  useEffect(() => {
    if (!usePortal) {
      return;
    }

    let scrollParent: HTMLElement = null;
    if (datePickerInputState.datePickerRef.current) {
      scrollParent = getOverflowParent(datePickerInputState.datePickerRef.current);
    }

    const handleScroll = () => updatePosition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    if (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
      scrollableParentElementRef.current = scrollParent;
    } else {
      scrollableParentElementRef.current = null;
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (scrollableParentElementRef.current) {
        scrollableParentElementRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [usePortal, updatePosition]);

  useEffect(() => {
    function handleWindowResize() {
      setDatePickerInputState(prev => {
        if (!prev.datePickerOpen) {
          return prev;
        }
        return { ...prev, datePickerOpen: false };
      });
    }

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  function formattedStringFromDateList(dates: Date[]) {
    let formattedString = "";

    const groupedYears: Dictionary<Date[]> = groupBy(dates, date => {
      return date.getFullYear();
    });

    Object.keys(groupedYears).forEach(year => {
      const yearlyDates: Date[] = groupedYears[year];

      const groupedMonths: Dictionary<Date[]> = groupBy(yearlyDates, yearlyDate => {
        return yearlyDate.getMonth();
      });

      let monthlyFormattedString = "";

      Object.keys(groupedMonths).forEach(month => {
        if (monthlyFormattedString !== "") {
          monthlyFormattedString += ", ";
        }

        const monthlyDates: Date[] = groupedMonths[month];

        monthlyFormattedString += monthlyDates[0].toLocaleString("default", { month: "short" });

        monthlyDates.forEach((monthDate, monthDateIndex) => {
          monthlyFormattedString += ` ${monthDate.getDate()}${monthDateIndex < monthlyDates.length - 1 ? "," : ""}`;
        });
      });

      if (formattedString !== "") {
        formattedString += "; ";
      }

      formattedString += monthlyFormattedString + " " + yearlyDates[0].toLocaleString("default", { year: "numeric" });
    });

    return formattedString;
  }

  const formattedMultipleDates = useMemo(() => {
    if (datePickerView !== "multiple" || multipleDates.length === 0) {
      return "Select Dates";
    }

    return formattedStringFromDateList(multipleDates);
  }, [multipleDates]);

  function getStartingAndEndingDatesWithDefault(): IDatePickerInputDateRange {
    const dateRange: IDatePickerInputDateRange = {
      start: undefined,
      end: undefined,
    };

    /* If there isn't a starting date default to the current date and
       check if the ending date occurs before it and if it does replace it
       with the current date as well
    */
    if (!startingDate) {
      dateRange.start = new Date();

      if (endingDate && dateIsGreaterThanOrEqual(dateRange.start, endingDate)) {
        dateRange.end = new Date();
      } else {
        dateRange.end = endingDate;
      }

      return dateRange;
    } else {
      // If there is already a starting date then leave it alone
      dateRange.start = startingDate;
      dateRange.end = endingDate;

      return dateRange;
    }
  }

  function resetDatePickerToInitialState() {
    let newFirstVisibleMonth: Date = null;
    let dateRange: IDatePickerInputDateRange = null;

    if (datePickerView === "multiple") {
      if (multipleDates.length === 0) {
        const today = new Date();

        newFirstVisibleMonth = new Date(today.getFullYear(), today.getMonth());
      } else {
        newFirstVisibleMonth = new Date(multipleDates[0].getFullYear(), multipleDates[0].getMonth());
      }
    } else {
      dateRange = getStartingAndEndingDatesWithDefault();

      newFirstVisibleMonth = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth());
    }

    setDatePickerInputState(prev => ({
      ...prev,
      firstVisibleMonth: newFirstVisibleMonth,
      selectedStartingDate: undefined,
      selectedStartingDateInput: !dateRange?.start ? "" : dateToFormattedDateString(dateRange.start),
      selectedEndingDate: undefined,
      selectedEndingDateInput: !dateRange?.end ? "" : dateToFormattedDateString(dateRange.end),
      selectedQuickOption: undefined,
    }));
  }

  useEffect(() => {
    if (!datePickerInputState.datePickerOpen) {
      resetDatePickerToInitialState();
    }
  }, [datePickerInputState.datePickerOpen]);

  useLayoutEffect(() => {
    if (isTabletSize || !datePickerInputState.datePickerOpen || verticalPosition === "top") {
      setDesktopPickerMaxHeight(null);
      setDesktopPickerOpenAbove(false);
      return;
    }

    const trigger = datePickerInputState.datePickerRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 10;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const estimatedHeight = desktopPickerPanelRef.current?.offsetHeight || 360;
    const openAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setDesktopPickerOpenAbove(openAbove);
    setDesktopPickerMaxHeight(Math.max(openAbove ? spaceAbove : spaceBelow, 120));
  }, [isTabletSize, datePickerInputState.datePickerOpen]);

  useEffect(() => {
    if (!allowEmptySingleDate && !startingDate && setStartingDate) {
      const dateRange = getStartingAndEndingDatesWithDefault();
      setStartingDate(dateRange.start);

      if (setEndingDate) {
        setEndingDate(dateRange.end);
      }
    }

    if (startingDate && !endingDate) {
      setDatePickerInputState(prev => ({
        ...prev,
        datePickerInput: dateToFormattedDateString(startingDate),
      }));
    }

    resetDatePickerToInitialState();
  }, [startingDate, endingDate]);

  useEffect(() => {
    if (datePickerInputState.selectedStartingDate) {
      setDatePickerInputState(prev => ({
        ...prev,
        selectedStartingDateInput: dateToFormattedDateString(datePickerInputState.selectedStartingDate),
      }));
    }
    if (datePickerInputState.selectedEndingDate) {
      setDatePickerInputState(prev => ({
        ...prev,
        selectedEndingDateInput: dateToFormattedDateString(datePickerInputState.selectedEndingDate),
      }));
    }
    if (datePickerInputState.selectedStartingDate && !datePickerInputState.selectedEndingDate) {
      setDatePickerInputState(prev => ({
        ...prev,
        selectedEndingDateInput: "",
      }));
    }
  }, [datePickerInputState.selectedStartingDate, datePickerInputState.selectedEndingDate]);

  function formattedDateStringIsValid(value: string) {
    // mm/dd - mm/dd/yyy
    const regex = monthOnlyCalendar ? FORMAT_DATE_STRING_NO_YEAR_REGEX : FORMAT_DATE_STRING_REGEX;
    const matchedDateValues = regex.exec(value);

    const month = parseInt(matchedDateValues?.[1]);
    const day = parseInt(matchedDateValues?.[2]);
    const year = parseInt(matchedDateValues?.[3]);

    const daysInMonth = new Date(year, month, 0)?.getDate();

    if (!month || !day || month < 1 || month > 12 || day < 1 || day > daysInMonth) {
      return false;
    }

    const date = new Date(value);

    if (disablePastDays && dateIsLessThan(date, getDayFromOffset({}))) {
      return false;
    }

    if (disableFutureDays && dateIsGreaterThan(date, getDayFromOffset({}))) {
      return false;
    }

    // Check if the date exceeds maxDayRange for range date pickers
    if (maxDayRange && datePickerView === "range") {
      const referenceDate = datePickerInputState.selectedStartingDate || startingDate;
      if (referenceDate) {
        const daysDifference = Math.abs(Math.floor((date.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24)));
        if (daysDifference > maxDayRange - 1) {
          return false;
        }
      }
    }

    return true;
  }

  function getDatePickerPositionStyles() {
    if (verticalPosition === "top") {
      const verticalTranslate = "-110%";
      if (position === "left") {
        return { left: "0%", transform: `translate(0%, ${verticalTranslate})` };
      } else if (position === "center") {
        return { left: "50%", transform: `translate(-50%, ${verticalTranslate})` };
      } else if (position === "right") {
        return { left: "100%", transform: `translate(-100%, ${verticalTranslate})` };
      }
      return;
    }

    const verticalOffset = desktopPickerOpenAbove ? -10 : 10;
    const aboveStyles = desktopPickerOpenAbove ? { top: "auto", bottom: "100%" } : {};

    if (position === "left") {
      return { left: "0%", transform: `translate(0%, ${verticalOffset}px)`, ...aboveStyles };
    } else if (position === "center") {
      return { left: "50%", transform: `translate(-50%, ${verticalOffset}px)`, ...aboveStyles };
    } else if (position === "right") {
      return { left: "100%", transform: `translate(-100%, ${verticalOffset}px)`, ...aboveStyles };
    }
  }

  function datesAreEqual(first: Date, second: Date) {
    if (first === undefined || second === undefined) {
      return false;
    } else {
      return (
        first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate()
      );
    }
  }

  function dateIsGreaterThan(first: Date, second: Date) {
    if (first === undefined || second === undefined) {
      return false;
    } else {
      const firstDate = new Date(first.getFullYear(), first.getMonth(), first.getDate());
      const secondDate = new Date(second.getFullYear(), second.getMonth(), second.getDate());

      return firstDate > secondDate;
    }
  }

  function dateIsGreaterThanOrEqual(first: Date, second: Date) {
    return dateIsGreaterThan(first, second) || datesAreEqual(first, second);
  }

  function dateIsLessThan(first: Date, second: Date) {
    if (first === undefined || second === undefined) {
      return false;
    } else {
      const firstDate = new Date(first.getFullYear(), first.getMonth(), first.getDate());
      const secondDate = new Date(second.getFullYear(), second.getMonth(), second.getDate());

      return firstDate < secondDate;
    }
  }

  function dateIsLessThanOrEqual(first: Date, second: Date) {
    return dateIsLessThan(first, second) || datesAreEqual(first, second);
  }

  function getDaysFromPreviousMonthInCurrentMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth()).getDay();
  }

  // Use leap year to display Feb 29th in case a course wants to use leap year day
  const leapYear = 2024;
  function getDaysInMonth(date: Date) {
    return 32 - new Date(monthOnlyCalendar ? leapYear : date.getFullYear(), date.getMonth(), 32).getDate();
  }

  function getStartAndEndDaysInPreviousMonth(date: Date): [number, number] {
    const previousMonth = new Date(date.getFullYear(), date.getMonth() - 1);
    const totalDaysInPreviousMonth = getDaysInMonth(previousMonth);
    const daysFromPreviousMonthInCurrentMonth = getDaysFromPreviousMonthInCurrentMonth(date);

    return [totalDaysInPreviousMonth - (daysFromPreviousMonthInCurrentMonth - 1), totalDaysInPreviousMonth + 1];
  }

  function getDaysInNextMonth(date: Date) {
    return 7 * 6 - (getDaysFromPreviousMonthInCurrentMonth(date) + getDaysInMonth(date));
  }

  function shiftMonth(date: Date, monthsToShiftBy: number) {
    const shiftedMonth = new Date(date.getFullYear(), date.getMonth());

    shiftedMonth.setMonth(date.getMonth() + monthsToShiftBy);
    return shiftedMonth;
  }

  function shiftFirstVisibleMonth(monthsToShiftBy: number) {
    const newFirstVisibleMonth = shiftMonth(datePickerInputState.firstVisibleMonth, monthsToShiftBy);
    setDatePickerInputState(prev => ({ ...prev, firstVisibleMonth: newFirstVisibleMonth }));
  }

  function handleSelectCalendarDay(disableSelect: boolean, day: Date, keepDatePickerOpen = false) {
    if (disableSelect) {
      return;
    } else if (datePickerView === "range") {
      if (datePickerInputState.selectedStartingDate === undefined) {
        setDatePickerInputState(prev => ({ ...prev, selectedStartingDate: day }));
      } else if (datePickerInputState.selectedEndingDate === undefined) {
        const [start, end] = [datePickerInputState.selectedStartingDate, day].sort(
          (a, b) => a?.getTime() - b?.getTime(),
        );
        setDatePickerInputState(prev => ({ ...prev, selectedStartingDate: start, selectedEndingDate: end }));
      } else {
        setDatePickerInputState(prev => ({ ...prev, selectedStartingDate: day, selectedEndingDate: undefined }));
      }
      setDatePickerInputState(prev => ({ ...prev, selectedQuickOption: undefined }));
    } else if (datePickerView === "single") {
      if (requireApply) {
        setDatePickerInputState(prev => ({
          ...prev,
          selectedStartingDate: day,
        }));
      } else {
        setStartingDate(day);
        setDatePickerInputState(prev => ({
          ...prev,
          datePickerOpen: keepDatePickerOpen,
          datePickerInput: dateToFormattedDateString(day),
        }));
      }
    } else if (datePickerView === "multiple") {
      let updatedMultipleDates: Date[] = null;

      if (multipleDates.some(multipleDate => datesAreEqual(multipleDate, day))) {
        updatedMultipleDates = multipleDates.filter(multipleDate => !datesAreEqual(multipleDate, day));
      } else {
        updatedMultipleDates = [...multipleDates, day];
      }

      updatedMultipleDates.sort((prev, next) => {
        if (datesAreEqual(prev, next)) {
          return 0;
        } else if (dateIsGreaterThan(prev, next)) {
          return 1;
        } else {
          return -1;
        }
      });

      setMultipleDates(updatedMultipleDates);
    }
  }

  type CalendarDayVisibleMonthRelation = "previous" | "current" | "next";
  function renderCalendarDay(
    monthRelation: CalendarDayVisibleMonthRelation,
    day: number,
    visible: boolean,
    visibleMonth: Date,
  ) {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth() + (monthRelation === "current" ? 0 : monthRelation === "previous" ? -1 : 1);
    const calendarDay = new Date(year, month, day);
    let dayIsDisabled =
      (disablePastDays && dateIsLessThan(calendarDay, new Date())) ||
      (disableFutureDays && dateIsGreaterThan(calendarDay, new Date()));

    // Check if the day exceeds the maxDayRange when a starting date is selected
    if (
      maxDayRange &&
      datePickerView === "range" &&
      datePickerInputState.selectedStartingDate &&
      !datePickerInputState.selectedEndingDate
    ) {
      const daysDifference = Math.abs(
        Math.floor(
          (calendarDay.getTime() - datePickerInputState.selectedStartingDate.getTime()) / (1000 * 60 * 60 * 24),
        ),
      );
      if (daysDifference > maxDayRange - 1) {
        dayIsDisabled = true;
      }
    }

    let dayIsSelected = false;
    let dayIsSelectedTwice = false;
    let dayIsInDateRange = false;

    if (datePickerInputState.selectedStartingDate || datePickerInputState.selectedEndingDate) {
      dayIsSelected =
        datesAreEqual(datePickerInputState.selectedStartingDate, calendarDay) ||
        datesAreEqual(datePickerInputState.selectedEndingDate, calendarDay);
      dayIsSelectedTwice =
        datesAreEqual(datePickerInputState.selectedStartingDate, calendarDay) &&
        datesAreEqual(datePickerInputState.selectedEndingDate, calendarDay);

      if (
        dateIsGreaterThanOrEqual(calendarDay, datePickerInputState.selectedStartingDate) &&
        dateIsGreaterThanOrEqual(datePickerInputState.selectedEndingDate, calendarDay)
      ) {
        dayIsInDateRange = true;
      }
    } else if (datePickerView === "single") {
      const selectedDate =
        requireApply && datePickerInputState.datePickerOpen ? datePickerInputState.selectedStartingDate : startingDate;

      if (selectedDate) {
        dayIsSelected = datesAreEqual(selectedDate, calendarDay);
      }
    } else if (startingDate || endingDate) {
      dayIsSelected = datesAreEqual(startingDate, calendarDay) || datesAreEqual(endingDate, calendarDay);
      dayIsSelectedTwice = datesAreEqual(startingDate, calendarDay) && datesAreEqual(endingDate, calendarDay);

      if (dateIsGreaterThanOrEqual(calendarDay, startingDate) && dateIsLessThanOrEqual(calendarDay, endingDate)) {
        dayIsInDateRange = true;
      }
    } else if (multipleDates) {
      if (multipleDates.some(multipleDate => datesAreEqual(multipleDate, calendarDay))) {
        dayIsSelected = true;
      }
    }

    let calendarDayContainerClass = "";
    let calendarDayClasses = "";

    if (dayIsDisabled) {
      calendarDayContainerClass = "date-picker-calendar-day-disabled-container";
    } else if (dayIsSelectedTwice) {
      calendarDayContainerClass = "date-picker-calendar-day-selected-twice-container";
    } else if (dayIsInDateRange) {
      calendarDayContainerClass = "date-picker-calendar-day-in-date-range-container";
    }

    calendarDayClasses += getClassNameWithResponsiveSize("date-picker-calendar-day");

    if (dayIsDisabled && monthRelation !== "current") {
      calendarDayClasses += " date-picker-calendar-day-disabled date-picker-calendar-day-outside-month";
    } else if (dayIsDisabled) {
      calendarDayClasses += " date-picker-calendar-day-disabled";
    } else if (dayIsSelected) {
      calendarDayClasses += " date-picker-calendar-day-selected";
    } else if (monthRelation !== "current") {
      calendarDayClasses += " date-picker-calendar-day-outside-month";
    }

    const isCurrentDay = calendarDay.toDateString() === getDayFromOffset({}).toDateString();

    return (
      <div
        className={classNames(calendarDayContainerClass, {
          "font-bold": !dayIsSelected && !dayIsInDateRange && isCurrentDay,
        })}
        style={{
          ...(dayIsInDateRange && !dayIsDisabled
            ? (() => {
                const isStart = datesAreEqual(calendarDay, datePickerInputState.selectedStartingDate ?? startingDate);
                const isEnd = datesAreEqual(calendarDay, datePickerInputState.selectedEndingDate ?? endingDate);
                const rangeColor = "#F2F4F7"; // matches $gray100 from variables

                if (dayIsSelectedTwice) {
                  return {
                    background: "transparent",
                    width: isTabletSize ? "32px" : "38px",
                    margin: "0 auto",
                    borderRadius: "50%",
                  };
                } else if (isStart) {
                  return {
                    background: `linear-gradient(to right, transparent 50%, ${rangeColor} 50%)`,
                    backgroundColor: "transparent",
                  };
                } else if (isEnd) {
                  return {
                    background: `linear-gradient(to left, transparent 50%, ${rangeColor} 50%)`,
                    backgroundColor: "transparent",
                  };
                }
                return {};
              })()
            : {}),
          visibility: visible ? "visible" : "hidden",
        }}
        onClick={() => handleSelectCalendarDay(dayIsDisabled, calendarDay)}
      >
        <div className={calendarDayClasses}>
          {day}
          {isCurrentDay && <span className={getClassNameWithResponsiveSize("day-circle")} />}
        </div>
      </div>
    );
  }

  function handleClearMultipleDates() {
    setMultipleDates([]);
  }

  function handleCancelDatePicker() {
    setDatePickerInputState(prev => ({ ...prev, datePickerOpen: false }));
  }

  function handleApplySelectedDate() {
    if (datePickerView === "range") {
      if (datePickerInputState.selectedStartingDate && datePickerInputState.selectedEndingDate && setEndingDate) {
        setStartingDate(datePickerInputState.selectedStartingDate);
        setEndingDate(datePickerInputState.selectedEndingDate);
        setDatePickerInputState(prev => ({ ...prev, datePickerOpen: false }));
      }
    } else if (datePickerView === "single" && requireApply) {
      if (datePickerInputState.selectedStartingDate && setStartingDate) {
        setStartingDate(datePickerInputState.selectedStartingDate);
        setDatePickerInputState(prev => ({
          ...prev,
          datePickerOpen: false,
          datePickerInput: dateToFormattedDateString(datePickerInputState.selectedStartingDate),
        }));
      }
    }
  }

  function getDayFromOffset(offset: { yearOffset?: number; monthOffset?: number; dayOffset?: number }) {
    const currentDate = new Date();
    const offsettedDate = new Date(
      currentDate.getFullYear() + (offset.yearOffset ?? 0),
      currentDate.getMonth() + (offset.monthOffset ?? 0),
      currentDate.getDate() + (offset.dayOffset ?? 0),
    );

    return offsettedDate;
  }

  function selectDateRange(id: number, select: () => void) {
    setDatePickerInputState(prev => ({
      ...prev,
      selectedQuickOption: id,
    }));

    select();
  }

  function selectToday() {
    const today = getDayFromOffset({});
    const newFirstVisibleMonth = new Date(today.getFullYear(), today.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: today,
      selectedEndingDate: today,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectYesterday() {
    const yesterday = getDayFromOffset({ dayOffset: -1 });
    const newFirstVisibleMonth = new Date(yesterday.getFullYear(), yesterday.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: yesterday,
      selectedEndingDate: yesterday,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectThisWeek() {
    const currentDate = new Date();
    const startOfWeek = getDayFromOffset({ dayOffset: currentDate.getDay() * -1 });
    const endOfWeek = getDayFromOffset({ dayOffset: 6 - currentDate.getDay() });
    const newFirstVisibleMonth = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfWeek,
      selectedEndingDate: endOfWeek,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectLastWeek() {
    const currentDate = new Date();
    const startOfLastWeek = getDayFromOffset({ dayOffset: currentDate.getDay() * -1 - 7 });
    const endOfLastWeek = getDayFromOffset({ dayOffset: currentDate.getDay() * -1 - 1 });
    const newFirstVisibleMonth = new Date(startOfLastWeek.getFullYear(), startOfLastWeek.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfLastWeek,
      selectedEndingDate: endOfLastWeek,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectPast7Days() {
    const today = getDayFromOffset({});
    const sevenDaysAgo = getDayFromOffset({ dayOffset: -6 });
    const newFirstVisibleMonth = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: sevenDaysAgo,
      selectedEndingDate: today,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectThisMonth() {
    const currentDate = new Date();

    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth());
    const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0);

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfMonth,
      selectedEndingDate: endOfMonth,
      firstVisibleMonth: startOfMonth,
    }));
  }

  function selectLastMonth() {
    const currentDate = new Date();

    const startOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const endOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfLastMonth,
      selectedEndingDate: endOfLastMonth,
      firstVisibleMonth: startOfLastMonth,
    }));
  }

  function selectThisYear() {
    const currentDate = new Date();

    const startOfYear = new Date(currentDate.getFullYear(), 0);
    const newFirstVisibleMonth = new Date(currentDate.getFullYear(), currentDate.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfYear,
      selectedEndingDate: currentDate,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function selectLastYear() {
    const currentDate = new Date();

    const startOfLastYear = new Date(currentDate.getFullYear() - 1, 0);
    const endOfLastYear = new Date(currentDate.getFullYear(), 0, 0);
    const newFirstVisibleMonth = new Date(startOfLastYear.getFullYear(), startOfLastYear.getMonth());

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDate: startOfLastYear,
      selectedEndingDate: endOfLastYear,
      firstVisibleMonth: newFirstVisibleMonth,
    }));
  }

  function openDatePicker() {
    if (disabled) {
      return;
    }
    setDatePickerInputState(prev => ({
      ...prev,
      datePickerOpen: true,
      ...(datePickerView === "single" && requireApply && startingDate ? { selectedStartingDate: startingDate } : {}),
    }));
  }

  function handleToggleDatePicker() {
    setDatePickerInputState(prev => {
      if (!prev.datePickerOpen) {
        return {
          ...prev,
          datePickerOpen: true,
          ...(datePickerView === "single" && requireApply && startingDate
            ? { selectedStartingDate: startingDate }
            : {}),
        };
      }

      return { ...prev, datePickerOpen: false };
    });
  }

  function handleOpenDatePickerOnFocus() {
    openDatePicker();
  }

  function changeDatePickerInput(event: any) {
    const updatedDatePickerInput = event.target.value as string;

    setDatePickerInputState(prev => ({
      ...prev,
      datePickerInput: updatedDatePickerInput,
    }));

    if (formattedDateStringIsValid(updatedDatePickerInput)) {
      const matchDatePickerDate = updatedDatePickerInput.match(FORMAT_DATE_STRING_REGEX);

      const month = parseInt(matchDatePickerDate[1]) - 1;
      const day = parseInt(matchDatePickerDate[2]);
      const year = parseInt(matchDatePickerDate[3]);

      const newDatePickerInputDate = new Date(year, month, day);

      const newFirstVisibleMonth = new Date(newDatePickerInputDate.getFullYear(), newDatePickerInputDate.getMonth());

      handleSelectCalendarDay(false, newDatePickerInputDate, true);
      setDatePickerInputState(prev => ({
        ...prev,
        firstVisibleMonth: newFirstVisibleMonth,
      }));
    }
  }

  function changeSelectedStartingDateInput(event: any) {
    const updatedSelectedStartingDateInput = event.target.value as string;

    setDatePickerInputState(prev => ({
      ...prev,
      selectedStartingDateInput: updatedSelectedStartingDateInput,
    }));

    if (formattedDateStringIsValid(updatedSelectedStartingDateInput)) {
      const matchedStartingDate = updatedSelectedStartingDateInput.match(
        monthOnlyCalendar ? FORMAT_DATE_STRING_NO_YEAR_REGEX : FORMAT_DATE_STRING_REGEX,
      );

      const month = parseInt(matchedStartingDate[1]) - 1;
      const day = parseInt(matchedStartingDate[2]);
      const year = parseInt(matchedStartingDate[3]);

      const newSelectedStartingDate = new Date(monthOnlyCalendar ? 2024 : year, month, day);

      const [start, end] = [newSelectedStartingDate, datePickerInputState.selectedEndingDate].sort(
        (a, b) => a?.getTime() - b?.getTime(),
      );

      const startInput = monthOnlyCalendar
        ? dateToFormattedDateString(start).slice(0, 5)
        : dateToFormattedDateString(start);
      const endInput = monthOnlyCalendar ? dateToFormattedDateString(end).slice(0, 5) : dateToFormattedDateString(end);

      const newFirstVisibleMonth = new Date(monthOnlyCalendar ? 2024 : start.getFullYear(), start.getMonth());

      setDatePickerInputState(prev => ({
        ...prev,
        selectedStartingDate: start,
        selectedStartingDateInput: startInput,
        selectedEndingDate: end,
        selectedEndingDateInput: endInput,
        firstVisibleMonth: newFirstVisibleMonth,
      }));
    }
  }

  function changeSelectedEndingDateInput(event: any) {
    const updatedSelectedEndingDateInput = event.target.value as string;

    setDatePickerInputState(prev => ({
      ...prev,
      selectedEndingDateInput: updatedSelectedEndingDateInput,
    }));

    if (formattedDateStringIsValid(updatedSelectedEndingDateInput)) {
      const matchedEndingDate = updatedSelectedEndingDateInput.match(
        monthOnlyCalendar ? FORMAT_DATE_STRING_NO_YEAR_REGEX : FORMAT_DATE_STRING_REGEX,
      );

      const month = parseInt(matchedEndingDate[1]) - 1;
      const day = parseInt(matchedEndingDate[2]);
      const year = parseInt(matchedEndingDate[3]);

      const newSelectedEndingDate = new Date(monthOnlyCalendar ? 2024 : year, month, day);

      const [start, end] = [
        datePickerInputState.selectedStartingDate ?? newSelectedEndingDate,
        newSelectedEndingDate,
      ].sort((a, b) => a?.getTime() - b?.getTime());

      const startInput = monthOnlyCalendar
        ? dateToFormattedDateString(start).slice(0, 5)
        : dateToFormattedDateString(start);
      const endInput = monthOnlyCalendar ? dateToFormattedDateString(end).slice(0, 5) : dateToFormattedDateString(end);

      const newFirstVisibleMonth = new Date(
        monthOnlyCalendar ? 2024 : end.getFullYear(),
        end.getMonth() - (months - 1),
      );

      setDatePickerInputState(prev => ({
        ...prev,
        selectedStartingDate: start,
        selectedStartingDateInput: startInput,
        selectedEndingDate: end,
        selectedEndingDateInput: endInput,
        firstVisibleMonth: newFirstVisibleMonth,
      }));
    }
  }

  function renderDateInputRanges() {
    return (
      <div
        className="date-picker-date-ranges-container"
        style={{
          ...(months > 1 ? { gap: "10px" } : {}),
        }}
      >
        <DatePickerBlurTitleInput
          value={datePickerInputState.selectedStartingDateInput}
          containerClassName={getClassNameWithResponsiveSize("date-picker-date-ranges-date")}
          calendarIconClassName="date-picker-input-calendar-icon"
          centeredInputText={false}
          error={!formattedDateStringIsValid(datePickerInputState.selectedStartingDateInput)}
          errorTitle={t("Invalid Date")}
          showCalendarIcon={false}
          blurTitle={dateToReadableString(new Date(datePickerInputState.selectedStartingDateInput))}
          showErrorOnEmptyString={false}
          showBlurTitleOnError={false}
          onChange={changeSelectedStartingDateInput}
          disabled={disabled}
        />
        <div className="date-picker-date-ranges-dash">-</div>
        <DatePickerBlurTitleInput
          value={datePickerInputState.selectedEndingDateInput}
          containerClassName={getClassNameWithResponsiveSize("date-picker-date-ranges-date")}
          calendarIconClassName="date-picker-input-calendar-icon"
          centeredInputText={false}
          error={!formattedDateStringIsValid(datePickerInputState.selectedEndingDateInput)}
          errorTitle={t("Invalid Date")}
          showCalendarIcon={false}
          blurTitle={dateToReadableString(new Date(datePickerInputState.selectedEndingDateInput))}
          showErrorOnEmptyString={false}
          showBlurTitleOnError={false}
          onChange={changeSelectedEndingDateInput}
          disabled={disabled}
        />
      </div>
    );
  }

  function areRangeViewDatesOutOfBounds() {
    const today = getDayFromOffset({});

    if (!disablePastDays) {
      return false;
    } else if (datePickerView === "range") {
      return dateIsLessThan(startingDate, today) || dateIsLessThan(endingDate, today);
    }
  }

  function areMultipleViewDatesOutOfBounds() {
    const today = getDayFromOffset({});

    if (!disablePastDays) {
      return false;
    } else if (datePickerView === "multiple") {
      return multipleDates.some(date => dateIsLessThan(date, today));
    }
  }

  function getClassNameWithResponsiveSize(className: string) {
    const responsiveSize = windowSize.width > TABLET_WIDTH ? size : "small";

    return `${className}-${responsiveSize}`;
  }

  const showApplyButton = datePickerView === "range" || (datePickerView === "single" && requireApply);
  const useCancelLabel = datePickerView === "range" || (datePickerView === "single" && requireApply);
  const useCompactApplyActions = months === 1 && showApplyButton;

  function getSingleBorderStyles() {
    const hasError = error;
    const isFocusedError = datePickerInputState.singleDatePickerInputContainerFocused && hasError;
    const isError = !datePickerInputState.singleDatePickerInputContainerFocused && hasError;
    const isFocused = datePickerInputState.singleDatePickerInputContainerFocused && !hasError;

    return {
      "focused-error": isFocusedError,
      error: isError,
      focused: isFocused,
    };
  }

  function getRangeBorderStyles() {
    const hasError = error || areRangeViewDatesOutOfBounds();
    const isFocusedError = datePickerInputState.rangeDatePickerInputContainerFocused && hasError;
    const isError = !datePickerInputState.rangeDatePickerInputContainerFocused && hasError;
    const isFocused = datePickerInputState.rangeDatePickerInputContainerFocused && !hasError;

    return {
      "focused-error": isFocusedError,
      error: isError,
      focused: isFocused,
    };
  }

  function getMultipleBorderStyles() {
    const hasError = error || areMultipleViewDatesOutOfBounds();
    const isFocusedError = datePickerInputState.multipleDatePickerInputContainerFocused && hasError;
    const isError = !datePickerInputState.multipleDatePickerInputContainerFocused && hasError;
    const isFocused = datePickerInputState.multipleDatePickerInputContainerFocused && !hasError;

    return {
      "focused-error": isFocusedError,
      error: isError,
      focused: isFocused,
    };
  }

  function getMobileDatePickerPositionStyles() {
    return datePickerInputState.datePickerOpen ? { transform: "translateY(0)" } : { transform: "translateY(100%)" };
  }

  function clearDate(event?: React.MouseEvent) {
    event?.stopPropagation();

    ReactDOM.unstable_batchedUpdates(() => {
      if (setStartingDate) {
        setStartingDate(null);
      }
      if (setEndingDate) {
        setEndingDate(null);
      }
    });

    setDatePickerInputState(prev => ({
      ...prev,
      datePickerInput: "",
      selectedStartingDate: undefined,
      selectedEndingDate: undefined,
    }));
  }

  function renderDatePickerPanelContent() {
    return (
      <>
        {showQuickOptions && !hideQuickOptions && (
          <div className="date-picker-quick-options-section">
            {quickOptions
              .filter(option => !monthOnlyCalendar || !["This year"].includes(option.label))
              .map((quickOption, index) => {
                return (
                  <div
                    key={index}
                    className={classNames(getClassNameWithResponsiveSize("date-picker-quick-option"), {
                      "date-picker-quick-option-selected": datePickerInputState.selectedQuickOption === index,
                    })}
                    onClick={() => selectDateRange(index, quickOption.onClick)}
                  >
                    {quickOption.label}
                  </div>
                );
              })}
          </div>
        )}
        <div className="date-picker-selection-section">
          <div className="date-picker-calendars-section">
            {range(1, months + 1).map((month, index) => {
              const currentVisibleMonth = shiftMonth(datePickerInputState.firstVisibleMonth, index);

              return (
                <div
                  key={`month-${month}`}
                  className="date-picker-calendar-section"
                  style={{
                    ...(month < months ? { borderRight: "1px solid lightgrey" } : {}),
                  }}
                >
                  <div className={getClassNameWithResponsiveSize("date-picker-month-shifter-container")}>
                    <button
                      type="button"
                      className={getClassNameWithResponsiveSize("date-picker-month-shifter-shift-button")}
                      style={{
                        visibility:
                          month === 1 && (!monthOnlyCalendar || currentVisibleMonth.getMonth() > 0)
                            ? "visible"
                            : "hidden",
                      }}
                      onClick={() => shiftFirstVisibleMonth(-1)}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <div className="date-picker-month-shifter-month">
                      {capitalizeWords(
                        currentVisibleMonth.toLocaleDateString(i18n?.language ?? "en-ca", {
                          month: "long",
                          ...(!monthOnlyCalendar && { year: "numeric" }),
                        }),
                      )}
                    </div>
                    <button
                      type="button"
                      className={getClassNameWithResponsiveSize("date-picker-month-shifter-shift-button")}
                      style={{
                        visibility:
                          month === months && (!monthOnlyCalendar || currentVisibleMonth.getMonth() < 11)
                            ? "visible"
                            : "hidden",
                      }}
                      onClick={() => shiftFirstVisibleMonth(1)}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                  {datePickerView === "single" && (
                    <Button
                      size={isTabletSize ? "medium" : size}
                      className="date-picker-today-date-btn"
                      type="secondary"
                      onClick={() =>
                        handleSelectCalendarDay(datesAreEqual(startingDate, getDayFromOffset({})), getDayFromOffset({}))
                      }
                    >
                      {t("Today")}
                    </Button>
                  )}
                  {datePickerView === "range" && months === 1 && <>{renderDateInputRanges()}</>}
                  <div className={getClassNameWithResponsiveSize("date-picker-calendar")}>
                    {!hideDaysOfWeek
                      ? moment.weekdaysShort().map(weekDay => {
                          return (
                            <div
                              key={weekDay}
                              className={getClassNameWithResponsiveSize("date-picker-calendar-week-day")}
                            >
                              {weekDay?.substring(0, 3)}
                            </div>
                          );
                        })
                      : null}
                    {/* {moment.weekdaysShort().map(weekDay => (
                      <div key={weekDay} className={getClassNameWithResponsiveSize("date-picker-calendar-week-day")}>
                        {weekDay.substring(0, 3)}
                      </div>
                    ))} */}
                    {range(...getStartAndEndDaysInPreviousMonth(currentVisibleMonth)).map(day => {
                      return (
                        <React.Fragment key={`previous-month-day-${day}`}>
                          {renderCalendarDay("previous", day, months === 1, currentVisibleMonth)}
                        </React.Fragment>
                      );
                    })}
                    {range(1, getDaysInMonth(currentVisibleMonth) + 1).map(day => {
                      return (
                        <React.Fragment key={`current-month-day-${day}`}>
                          {renderCalendarDay("current", day, true, currentVisibleMonth)}
                        </React.Fragment>
                      );
                    })}
                    {range(1, getDaysInNextMonth(currentVisibleMonth) + 1).map(day => {
                      return (
                        <React.Fragment key={`next-month-day-${day}`}>
                          {renderCalendarDay("next", day, months === 1, currentVisibleMonth)}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="date-picker-actions-section">
            {months > 1 && (
              <div
                style={{
                  visibility: endingDate ? "visible" : "hidden",
                }}
              >
                {renderDateInputRanges()}
              </div>
            )}
            <div
              className={classNames("date-picker-actions", {
                "date-picker-actions-compact-apply": useCompactApplyActions,
              })}
              style={{
                ...(months === 1 ? { width: "100%" } : {}),
              }}
            >
              {datePickerView === "multiple" && (
                <Button
                  size={isTabletSize ? "medium" : size}
                  style={{
                    ...(months === 1 ? { flex: "1" } : {}),
                  }}
                  onClick={handleClearMultipleDates}
                >
                  <div className="date-picker-action">{t("Clear")}</div>
                </Button>
              )}
              <Button
                size={isTabletSize ? "medium" : size}
                type={datePickerView === "multiple" ? "primary" : "secondary"}
                style={{
                  ...(months === 1 ? { flex: "1" } : {}),
                }}
                onClick={handleCancelDatePicker}
              >
                <div className="date-picker-action">{useCancelLabel ? t("Cancel") : t("Close")}</div>
              </Button>
              {showApplyButton && (
                <Button
                  size={isTabletSize ? "medium" : size}
                  type="primary"
                  style={{
                    ...(months === 1 ? { flex: "1" } : {}),
                  }}
                  onClick={handleApplySelectedDate}
                  disabled={
                    datePickerView === "range"
                      ? datePickerInputState.selectedStartingDate === undefined ||
                        datePickerInputState.selectedEndingDate === undefined
                      : datePickerInputState.selectedStartingDate === undefined
                  }
                >
                  <div className="date-picker-action">{t("Apply")}</div>
                </Button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderMobileDatePickerOverlay() {
    return (
      <Portal isMounted={Boolean(datePickerInputState.firstVisibleMonth)}>
        <div ref={mobilePortalRef} className="date-picker-container date-picker-container--mobile-portal">
          {datePickerInputState.datePickerOpen && (
            <div
              className="date-picker-backdrop"
              onClick={() => setDatePickerInputState(prev => ({ ...prev, datePickerOpen: false }))}
            ></div>
          )}
          <div className="date-picker" style={getMobileDatePickerPositionStyles()}>
            {renderDatePickerPanelContent()}
          </div>
        </div>
      </Portal>
    );
  }

  /**
   * @description Returns the first overflow parent HTML element found from a starting HTML Element
   * @param {HTMLElement} element - Starting HTML Element
   * @returns HTMLElement
   */
  function getOverflowParent(element: HTMLElement): HTMLElement {
    const overflowYStyling = element && window.getComputedStyle(element).overflowY;
    const scrollable = overflowYStyling !== "visible" && overflowYStyling !== "hidden";

    if (!element) {
      return null;
    } else if (scrollable && element.scrollHeight >= element.clientHeight) {
      // check if element has overflow scrollable css property and the element's contents scroll height >= the element's height
      return element;
    }

    return getOverflowParent(element.parentElement) || document.body;
  }

  const DropdownWrapper = useCallback(({ usePortal, children }: { usePortal: boolean; children: React.ReactNode }) => {
    return usePortal ? (
      <Portal parentId={portalId}>
        <div className="date-picker-container">{children}</div>
      </Portal>
    ) : (
      <>{children}</>
    );
  }, []);

  return (
    <>
      {datePickerView !== "invalid" && datePickerInputState.firstVisibleMonth && (
        <div className={classNames({ "date-picker-wrapper-icon-only": iconOnlyTrigger })}>
          {label && <div className="date-picker-label">{label}</div>}
          <div
            ref={datePickerInputState.datePickerRef}
            className={classNames("date-picker-container", { "date-picker-container-icon-only": iconOnlyTrigger })}
          >
            {datePickerView === "range" && (
              <button
                disabled={disabled}
                type="button"
                className={classNames(
                  "date-picker-input-container",
                  { "date-picker-input-container-icon-only": iconOnlyTrigger },
                  getRangeBorderStyles(),
                )}
                onClick={handleToggleDatePicker}
                onFocus={() =>
                  setDatePickerInputState(prev => ({ ...prev, rangeDatePickerInputContainerFocused: true }))
                }
                onBlur={() =>
                  setDatePickerInputState(prev => ({ ...prev, rangeDatePickerInputContainerFocused: false }))
                }
              >
                <div
                  className="date-picker-input-range"
                  style={{ justifyContent: centeredInputText && !iconOnlyTrigger && "center" }}
                >
                  <FontAwesomeIcon
                    className={classNames("date-picker-input-calendar-icon", {
                      "date-picker-input-calendar-icon-only": iconOnlyTrigger,
                    })}
                    icon={faCalendar}
                  />

                  {!iconOnlyTrigger &&
                    (shorterDateString ? (
                      <>
                        {moment(startingDate).format("MMM. D")} - {moment(endingDate).format("MMM. D")}
                      </>
                    ) : (
                      <>
                        {dateToReadableString(startingDate)} - {dateToReadableString(endingDate)}
                      </>
                    ))}
                </div>
              </button>
            )}
            {datePickerView === "multiple" && (
              <button
                disabled={disabled}
                type="button"
                className={classNames(
                  "date-picker-input-container",
                  { "date-picker-input-container-icon-only": iconOnlyTrigger },
                  getMultipleBorderStyles(),
                )}
                onClick={handleToggleDatePicker}
                onFocus={() =>
                  setDatePickerInputState(prev => ({ ...prev, multipleDatePickerInputContainerFocused: true }))
                }
                onBlur={() =>
                  setDatePickerInputState(prev => ({ ...prev, multipleDatePickerInputContainerFocused: false }))
                }
              >
                <div
                  className="date-picker-input-multiple-dates"
                  style={{ justifyContent: centeredInputText && !iconOnlyTrigger && "center" }}
                >
                  <FontAwesomeIcon
                    className={classNames("date-picker-input-calendar-icon", {
                      "date-picker-input-calendar-icon-only": iconOnlyTrigger,
                    })}
                    icon={faCalendar}
                  />
                  {!iconOnlyTrigger && (
                    <div className="date-picker-input-multiple-dates-content">{formattedMultipleDates}</div>
                  )}
                </div>
              </button>
            )}
            {datePickerView === "single" &&
              (iconOnlyTrigger ? (
                <button
                  disabled={disabled}
                  type="button"
                  className={classNames(
                    "date-picker-input-container",
                    "date-picker-input-container-icon-only",
                    getSingleBorderStyles(),
                  )}
                  onClick={handleToggleDatePicker}
                  onFocus={() =>
                    setDatePickerInputState(prev => ({ ...prev, singleDatePickerInputContainerFocused: true }))
                  }
                  onBlur={() =>
                    setDatePickerInputState(prev => ({ ...prev, singleDatePickerInputContainerFocused: false }))
                  }
                >
                  <FontAwesomeIcon
                    className="date-picker-input-calendar-icon date-picker-input-calendar-icon-only"
                    icon={faCalendar}
                  />
                </button>
              ) : !startingDate && allowEmptySingleDate ? (
                <button
                  type="button"
                  className={classNames(
                    "date-picker-input-container",
                    "date-picker-blur-title-input-container",
                    { focused: datePickerInputState.datePickerOpen },
                    getSingleBorderStyles(),
                  )}
                  onClick={handleToggleDatePicker}
                >
                  <FontAwesomeIcon className="date-picker-input-calendar-icon" icon={faCalendar} />

                  <span>{t("Select date")}</span>
                </button>
              ) : (
                <div
                  className={classNames({
                    "flex items-center justify-between gap-4": allowClear && allowEmptySingleDate,
                  })}
                >
                  <DatePickerBlurTitleInput
                    value={datePickerInputState.datePickerInput}
                    containerClassName="date-picker-input-container"
                    calendarIconClassName="date-picker-input-calendar-icon"
                    centeredInputText={centeredInputText}
                    error={error || !formattedDateStringIsValid(datePickerInputState.datePickerInput)}
                    errorTitle={t("Invalid Date")}
                    showBlurTitleOnError={!!showBlurTitleOnError}
                    showCalendarIcon={true}
                    blurTitle={dateToReadableString(new Date(datePickerInputState.datePickerInput))}
                    showErrorOnEmptyString
                    onChange={changeDatePickerInput}
                    placeholder={t("Select date")}
                    revealText={datePickerInputState.datePickerOpen}
                    onFocus={handleOpenDatePickerOnFocus}
                    rightAlignedCalendar={rightAlignedCalendar}
                    disabled={disabled}
                  />

                  {allowClear && allowEmptySingleDate && (
                    <button type="button" onClick={clearDate} className="date-picker-clear-btn">
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              ))}
            {isTabletSize ? (
              renderMobileDatePickerOverlay()
            ) : (
              <DropdownWrapper usePortal={usePortal}>
                <div
                  ref={usePortal ? dropdownRef : desktopPickerPanelRef}
                  className={classNames("date-picker", {
                    "date-picker--open-above": !usePortal && desktopPickerOpenAbove,
                  })}
                  style={
                    usePortal
                      ? {
                          top:
                            selectPosition.top ??
                            datePickerInputState.datePickerRef.current?.getBoundingClientRect().bottom ??
                            0,
                          left:
                            selectPosition.left ??
                            datePickerInputState.datePickerRef.current?.getBoundingClientRect().left ??
                            0,
                          zIndex: 520,
                          maxHeight: Math.max(
                            window.innerHeight -
                              (datePickerInputState.datePickerRef.current?.getBoundingClientRect().bottom ?? 0) -
                              8,
                            0,
                          ),
                          minWidth: "fit-content",
                          position: "fixed",
                          display: datePickerInputState.datePickerOpen ? "flex" : "none",
                        }
                      : {
                          ...getDatePickerPositionStyles(),
                          display: datePickerInputState.datePickerOpen ? "flex" : "none",
                          ...(desktopPickerMaxHeight != null ? { maxHeight: `${desktopPickerMaxHeight}px` } : {}),
                        }
                  }
                >
                  {renderDatePickerPanelContent()}
                </div>
              </DropdownWrapper>
            )}
          </div>
          {helpText && (
            <div className="ui-input-help-text font-normal text-gray-400 mt-2">
              <span>{helpText}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
